import { Fragment, useEffect, useState } from 'react'
import { Button, Dropdown, Modal } from 'react-bootstrap'
import { connect } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getApprovalsByUser,
  getLimitExtensionEligibility,
  cancelApprovalRequest,
  getUserTransactionLimits,
  requestTransactionLimitExtension,
} from '../../../services/TransactionService'
import { toast } from 'react-toastify'
import useGuidedTour from '../../../hooks/useGuidedTour'
import { scrollToPageTop } from '../../../utils/scrollToPageTop'
import { showMutationError, makeFieldErrorClearer } from '../../../utils/showMutationError'
import {
  MY_REQUESTS_OVERVIEW_TOUR_ID,
  buildMyRequestsOverviewTourSteps,
} from './MyRequests.tour'

const getDisplayAmount = (approval) => approval.requested_amount ?? '-'

const statusBadgeClass = (slug) => {
  if (slug === 'approved') return 'badge-success'
  if (slug === 'pending')  return 'badge-warning'
  return 'badge-danger'
}

const formatDate = (dateString) =>
  new Date(dateString).toLocaleString('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })

const STATUS_OPTIONS = [
  { value: 'pending',  label: 'Pending'  },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
  if (lastPage <= 1) return null
  return (
    <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-decoration-none"
        disabled={page <= 1 || isLoading}
        onClick={onPrev}
        aria-label="Previous page"
      >
        <i className="fa fa-angle-left me-1" />
        Prev
      </button>
      <span className="text-muted small">Page {page} of {lastPage}</span>
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-decoration-none"
        disabled={page >= lastPage || isLoading}
        onClick={onNext}
        aria-label="Next page"
      >
        Next
        <i className="fa fa-angle-right ms-1" />
      </button>
    </div>
  )
}

const MyRequests = ({ partnerId, authUser }) => {
  const queryClient = useQueryClient()

  useGuidedTour(
    MY_REQUESTS_OVERVIEW_TOUR_ID,
    buildMyRequestsOverviewTourSteps(),
  )

  const [approvalToCancel, setApprovalToCancel] = useState(null)
  const [showCancelModal,  setShowCancelModal]  = useState(false)
  const [showAddModal,     setShowAddModal]     = useState(false)
  const [newRequest,       setNewRequest]       = useState({ reason: '', maxAmount: '', limitId: '' })
  const [newRequestFieldErrors, setNewRequestFieldErrors] = useState({})
  const [currentPage,      setCurrentPage]      = useState(1)
  const [activeStatus,     setActiveStatus]     = useState('pending')
  const [dateFrom,         setDateFrom]         = useState('')
  const [dateTo,           setDateTo]           = useState('')
  const [search,           setSearch]           = useState('')

  const userId = authUser.id

  const { data, isPending, isError, isFetching, error } = useQuery({
    queryKey: ['approvalRequestsByUser', partnerId, userId, activeStatus, search, dateFrom, dateTo, currentPage],
    queryFn:  () => getApprovalsByUser(partnerId, userId, { status: activeStatus, search, dateFrom, dateTo, page: currentPage }),
    enabled:  !!partnerId && !!userId,
    staleTime: 0,
  })

  const {
    data: userLimitsData,
    isError: userLimitsError,
    isPending: userLimitsLoading,
  } = useQuery({
    queryKey: ['userTransactionLimits', partnerId, userId],
    queryFn:  () => getUserTransactionLimits(partnerId, userId),
    enabled:  !!userId && !!partnerId,
    staleTime: 5 * 60 * 1000,
  })

  const {
    data: eligibilityData,
    isPending: eligibilityLoading,
  } = useQuery({
    queryKey: ['limitExtensionEligibility', partnerId, userId],
    queryFn:  () => getLimitExtensionEligibility(partnerId, userId),
    enabled:  !!userId && !!partnerId,
    staleTime: 5 * 60 * 1000,
  })

  const approvals    = data?.data?.data || []
  const totalPages   = data?.data?.meta?.last_page ?? 1
  const userLimits   = userLimitsData?.data?.data || []
  const hasNoLimits  = !userLimitsLoading && !userLimitsError && userLimits.length === 0
  const hasNoReviewer = !eligibilityLoading && eligibilityData?.data?.eligible === false

  const newRequestDisabled = userLimitsLoading || eligibilityLoading || hasNoLimits || hasNoReviewer
  const newRequestTitle    = hasNoLimits
    ? 'You have no transaction limits assigned yet. Ask an admin to assign one before requesting an extension.'
    : hasNoReviewer
      ? 'You are the only admin on this account. Invite a second admin from Users before requesting an extension so someone is accountable for approving it.'
      : undefined

  const currentStatusLabel = STATUS_OPTIONS.find(o => o.value === activeStatus)?.label ?? 'Pending'
  const fromLabel          = dateFrom ? `From ${dateFrom}` : 'From date'
  const toLabel            = dateTo   ? `To ${dateTo}`     : 'To date'
  const hasActiveFilter    = dateFrom !== '' || dateTo !== '' || search !== ''

  const handleStatusChange = (newStatus) => {
    setActiveStatus(newStatus)
    setCurrentPage(1)
  }

  const handleFilterChange = (setter) => (value) => {
    setter(value)
    setCurrentPage(1)
  }

  const handleClear = () => {
    setDateFrom('')
    setDateTo('')
    setSearch('')
    setCurrentPage(1)
  }

  useEffect(() => {
    if (error) toast.error('Failed to load approval requests. Please try again later.')
  }, [error])

  useEffect(() => {
    if (userLimitsError) toast.error('Failed to load transaction limits. Please try again.')
  }, [userLimitsError])

  const cancelMutation = useMutation({
    mutationFn: (approvalId) => cancelApprovalRequest(partnerId, approvalId),
    onSuccess: async () => {
      toast.success('Request cancelled.')
      await queryClient.invalidateQueries({ queryKey: ['approvalRequestsByUser', partnerId, userId] })
      setShowCancelModal(false)
      setApprovalToCancel(null)
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message ?? 'Failed to cancel request. Please try again.')
    },
  })

  const createRequestMutation = useMutation({
    mutationFn: (payload) => requestTransactionLimitExtension(partnerId, payload),
    onSuccess: async () => {
      toast.success('Request submitted successfully.')
      await queryClient.invalidateQueries({ queryKey: ['approvalRequestsByUser', partnerId, userId] })
      setShowAddModal(false)
      setNewRequest({ reason: '', maxAmount: '', limitId: '' })
      setNewRequestFieldErrors({})
    },
    onError: (err) => showMutationError(err, {
      fallback: 'Failed to submit request. Try again later.',
      onFieldErrors: setNewRequestFieldErrors,
    }),
  })

  const openCancelModal = (approval) => {
    setApprovalToCancel(approval)
    setShowCancelModal(true)
  }

  const closeCancelModal = () => {
    setShowCancelModal(false)
    setApprovalToCancel(null)
  }

  const handleCreateRequest = () => {
    setNewRequestFieldErrors({})
    createRequestMutation.mutate({
      request_reason:              newRequest.reason,
      max_amount:                  parseInt(newRequest.maxAmount, 10),
      user_transaction_limit_id:   newRequest.limitId,
    })
  }

  const clearNewRequestFieldError = makeFieldErrorClearer(setNewRequestFieldErrors)

  return (
    <Fragment>
      <div className="row">
        <div className="col-12">
          <div className="card" data-tour="my-requests-card">
            <div className="card-header d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
              <h4 className="card-title mb-0">Limit Extension Requests</h4>
              <button
                className="btn btn-sm btn-primary ms-sm-auto"
                onClick={() => setShowAddModal(true)}
                disabled={newRequestDisabled}
                title={newRequestTitle}
                aria-disabled={newRequestDisabled}
                data-tour="my-requests-new-request-button"
              >
                <i className="fa fa-plus me-1" aria-hidden="true" />
                New Request
              </button>
            </div>
            <div className="card-body">
              {hasNoLimits && (
                <div className="alert alert-warning py-2 mb-3" role="status">
                  You have no transaction limits assigned yet, so limit extension requests are not available. Ask an admin to assign one, then come back here to request an extension.
                </div>
              )}
              {!hasNoLimits && hasNoReviewer && (
                <div className="alert alert-warning py-2 mb-3" role="status">
                  You are the only admin on this account, so there is no one to approve a limit extension request. Invite a second admin from Users before requesting an extension so someone is accountable for approving it.
                </div>
              )}
              <div className="mb-3" data-tour="my-requests-filters">
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder="Search"
                  value={search}
                  onChange={e => handleFilterChange(setSearch)(e.target.value)}
                />
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                      <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {STATUS_OPTIONS.map(option => (
                        <Dropdown.Item
                          key={option.value}
                          active={activeStatus === option.value}
                          onClick={() => handleStatusChange(option.value)}
                        >
                          {option.label}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                      <span className="flex-grow-1 text-start">{fromLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateFrom}
                        onChange={e => handleFilterChange(setDateFrom)(e.target.value)}
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                      <span className="flex-grow-1 text-start">{toLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={dateTo}
                        onChange={e => handleFilterChange(setDateTo)(e.target.value)}
                      />
                    </Dropdown.Menu>
                  </Dropdown>
                  {hasActiveFilter && (
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                      onClick={handleClear}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body p-0" data-tour="my-requests-list">
              {isPending ? (
                <div className="d-flex justify-content-center py-4">
                  <span className="spinner-border spinner-border-sm text-primary" role="status" />
                </div>
              ) : isError ? (
                <p className="text-center text-danger py-4 mb-0">
                  Failed to load approval requests. Please try again.
                </p>
              ) : approvals.length === 0 ? (
                <p className="text-center text-muted py-4 mb-0">
                  No approval requests found.
                </p>
              ) : (
                <>
                  <div className="table-responsive d-none d-xl-block px-3 pt-2">
                    <table className="table verticle-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Date</th>
                          <th scope="col">Amount / Limit</th>
                          <th scope="col">Type</th>
                          <th scope="col">Reason</th>
                          <th scope="col">Status</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvals.map((approval) => (
                          <tr key={approval.id}>
                            <td className="text-nowrap">{formatDate(approval.created_on)}</td>
                            <td className="text-nowrap">{getDisplayAmount(approval)}</td>
                            <td className="text-nowrap">{approval.approval_request_type}</td>
                            <td style={{ maxWidth: '240px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {approval.request_reason || '-'}
                            </td>
                            <td>
                              <span className={`badge badge-rounded ${statusBadgeClass(approval.status_slug)}`}>
                                {approval.status_name}
                              </span>
                            </td>
                            <td>
                              {approval.status_slug === 'pending' && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openCancelModal(approval)}
                                  title="Cancel request"
                                  aria-label="Cancel request"
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                    <table className="table verticle-middle mb-0">
                      <thead>
                        <tr>
                          <th scope="col">Request</th>
                          <th scope="col">Status</th>
                          <th scope="col" className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {approvals.map((approval) => (
                          <tr key={approval.id}>
                            <td>
                              <div className="fw-semibold">{getDisplayAmount(approval)}</div>
                              <div className="small text-muted">{approval.approval_request_type}</div>
                              <div className="small text-muted mt-1">
                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                {formatDate(approval.created_on)}
                              </div>
                              {approval.request_reason && (
                                <div className="mt-2" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {approval.request_reason}
                                </div>
                              )}
                            </td>
                            <td>
                              <span className={`badge badge-rounded ${statusBadgeClass(approval.status_slug)}`}>
                                {approval.status_name}
                              </span>
                            </td>
                            <td className="text-end">
                              {approval.status_slug === 'pending' && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openCancelModal(approval)}
                                  title="Cancel request"
                                  aria-label="Cancel request"
                                >
                                  <i className="fa fa-trash" aria-hidden="true" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ul className="list-group list-group-flush d-block d-md-none">
                    {approvals.map((approval) => (
                      <li key={approval.id} className="list-group-item py-3">
                        <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                          <span className="fw-semibold text-body">{getDisplayAmount(approval)}</span>
                          <span className={`badge badge-rounded ${statusBadgeClass(approval.status_slug)}`}>
                            {approval.status_name}
                          </span>
                        </div>
                        <div className="small text-muted">{approval.approval_request_type}</div>
                        <div className="small text-muted">
                          <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                          {formatDate(approval.created_on)}
                        </div>
                        {approval.request_reason && (
                          <div className="mt-2" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {approval.request_reason}
                          </div>
                        )}
                        {approval.status_slug === 'pending' && (
                          <div className="mt-2">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => openCancelModal(approval)}
                              title="Cancel request"
                              aria-label="Cancel request"
                            >
                              <i className="fa fa-trash me-1" aria-hidden="true" />
                              Cancel
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
              <PaginationBar
                page={currentPage}
                lastPage={totalPages}
                isLoading={isFetching}
                onPrev={() => { setCurrentPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                onNext={() => { setCurrentPage(p => Math.min(p + 1, totalPages)); scrollToPageTop(); }}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showCancelModal} onHide={closeCancelModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cancel Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to cancel this limit extension request? This cannot be undone.</p>
          {approvalToCancel && (
            <div className="mt-2">
              <p className="mb-1"><strong>Amount / Limit:</strong> {getDisplayAmount(approvalToCancel)}</p>
              <p className="mb-0"><strong>Reason:</strong> {approvalToCancel.request_reason || '-'}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCancelModal} disabled={cancelMutation.isPending}>
            Keep Request
          </Button>
          <Button
            variant="danger"
            onClick={() => cancelMutation.mutate(approvalToCancel.id)}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                Cancelling...
              </>
            ) : 'Yes, Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>New Limit Extension Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={(e) => { e.preventDefault(); handleCreateRequest() }}>
            <div className="mb-3">
              <label className="form-label">Transaction Limit</label>
              <select
                className={`form-select${newRequestFieldErrors.user_transaction_limit_id ? ' is-invalid' : ''}`}
                value={newRequest.limitId}
                onChange={(e) => {
                  setNewRequest({ ...newRequest, limitId: e.target.value })
                  clearNewRequestFieldError('user_transaction_limit_id')
                }}
                required
              >
                <option value="">Select...</option>
                {userLimits.map((limit) => (
                  <option key={limit.id} value={limit.id}>
                    {limit.transaction_limit.description} (Max {limit.amount_limit})
                  </option>
                ))}
              </select>
              {newRequestFieldErrors.user_transaction_limit_id && (
                <div className="invalid-feedback d-block">{newRequestFieldErrors.user_transaction_limit_id}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Reason</label>
              <textarea
                className={`form-control${newRequestFieldErrors.request_reason ? ' is-invalid' : ''}`}
                rows={3}
                value={newRequest.reason}
                onChange={(e) => {
                  setNewRequest({ ...newRequest, reason: e.target.value })
                  clearNewRequestFieldError('request_reason')
                }}
                required
              />
              {newRequestFieldErrors.request_reason && (
                <div className="invalid-feedback d-block">{newRequestFieldErrors.request_reason}</div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">New Max Amount</label>
              <input
                type="number"
                className={`form-control${newRequestFieldErrors.max_amount ? ' is-invalid' : ''}`}
                value={newRequest.maxAmount}
                onChange={(e) => {
                  const value = e.target.value
                  if (/^\d*$/.test(value) && Number(value) <= 150000) {
                    setNewRequest({ ...newRequest, maxAmount: value })
                    clearNewRequestFieldError('max_amount')
                  }
                }}
                required
              />
              {newRequestFieldErrors.max_amount && (
                <div className="invalid-feedback d-block">{newRequestFieldErrors.max_amount}</div>
              )}
            </div>

            <div className="d-flex flex-wrap justify-content-end gap-2 pt-2">
              <Button
                variant="secondary"
                type="button"
                onClick={() => setShowAddModal(false)}
                disabled={createRequestMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={
                  !newRequest.reason ||
                  !newRequest.maxAmount ||
                  !newRequest.limitId ||
                  createRequestMutation.isPending
                }
              >
                {createRequestMutation.isPending ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Submitting...
                  </>
                ) : 'Submit Request'}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </Fragment>
  )
}

const mapStateToProps = (state) => ({
  partnerId: state.auth.authUser?.partner_id,
  authUser:  state.auth.authUser,
})

export default connect(mapStateToProps)(MyRequests)
